import {FC, useState} from 'react'
import PhoneInputWithCountrySelect from 'react-phone-number-input/react-hook-form'
import flags from 'react-phone-number-input/flags'
import {useFormContext} from 'react-hook-form'
import {I18n} from '../../common/i18n'
import parsePhoneNumberFromString, {CountryCode, getCountryCallingCode, isPossiblePhoneNumber} from 'libphonenumber-js'
import {RequiredAstrisk} from '../RequiredAstrisk'

interface PhoneNumberInputProps {
  senderCountryCode?: string
  i18n: ReturnType<typeof I18n.i18n>
}

interface FormValues {
  phoneNumber?: string
}

const PhoneNumberInput: FC<PhoneNumberInputProps> = ({i18n, senderCountryCode}) => {
  const {
    control,
    formState: {errors},
    trigger,
  } = useFormContext<FormValues>()

  const [phoneValue, setPhoneValue] = useState<string>('')

  const validatePhoneNumber = (value?: string) => {
    if (!value || value.trim() === '') {
      return i18n.error.required
    }
    const digits = value.replace(/\D/g, '')
    if (digits.length < 8) return i18n.error.invalidPhoneNumber
    if (!isPossiblePhoneNumber(value)) return i18n.error.invalidPhoneNumber
    return true
  }

  const handleChange = (value?: string) => {
    let normalized = value ?? ''
    const parsed = parsePhoneNumberFromString(normalized)
    if (parsed && senderCountryCode && parsed.country && parsed.country !== senderCountryCode) {
      const national = parsed.nationalNumber
      const dialCode = getCountryCallingCode(senderCountryCode as CountryCode)
      normalized = `+${dialCode}${national}`
    }
    setPhoneValue(normalized)
    void trigger('phoneNumber')
  }

  return (
    <label className="hw-label">
      {i18n.phoneNumber}
      <RequiredAstrisk />
      <PhoneInputWithCountrySelect
        className={errors.phoneNumber ? 'hw-input hw-input--error' : 'hw-input'}
        name="phoneNumber"
        data-testid="phoneNumberInputField"
        control={control}
        value={phoneValue}
        onChange={handleChange}
        flags={flags}
        defaultCountry={senderCountryCode ? (senderCountryCode as CountryCode) : undefined}
        rules={{
          validate: validatePhoneNumber,
        }}
        countrySelectProps={{
          className: 'hw-country-select',
          unicodeFlags: true,
        }}
      />
      {errors.phoneNumber && <span className="hw-error">{errors.phoneNumber?.message}</span>}
    </label>
  )
}

export default PhoneNumberInput
